'use client';

import { useState } from 'react';
import { cn, composeTailwindRenderProps } from '@utils';
import {
	composeRenderProps,
	TextArea as TextAreaPrimitive,
	TextField as TextFieldPrimitive,
} from 'react-aria-components';

import type {
	TextFieldProps as TextFieldPrimitiveProps,
	ValidationResult,
} from 'react-aria-components';

import { trimOnBlur } from '@/utils/fieldHelper';
import {
	Description,
	FieldError,
	fieldStyle,
	FieldSuccess,
	FieldVariant,
	FieldWidth,
	Label,
} from './Field';

interface TextAreaProps extends TextFieldPrimitiveProps {
	autoSize?: boolean;
	label?: string;
	hint?: string;
	hintChildren?: React.ReactNode;
	successMessage?: string;
	errorMessage?: string | ((validation: ValidationResult) => string);
	isValid?: boolean;
	placeholder?: string;
	theme?: 'light' | 'dark';
	variant?: FieldVariant;
	width?: FieldWidth;
	className?: string;
}

const TextArea = ({
	className,
	placeholder,
	label,
	hint,
	hintChildren,
	successMessage,
	errorMessage,
	isValid,
	pattern,
	theme,
	variant,
	width,
	...props
}: TextAreaProps) => {
	const [isFocusWithin, setIsFocusWithin] = useState(false);

	const handleBlur = (event: React.FocusEvent<HTMLInputElement, Element>) => {
		trimOnBlur(event, props.onChange, pattern, props.onBlur);
	};

	return (
		<TextFieldPrimitive
			{...props}
			onBlur={handleBlur}
			className={composeTailwindRenderProps(className, 'group flex flex-col gap-y-1.5')}
		>
			{label && <Label isRequired={props.isRequired}>{label}</Label>}
			{hint && <Description>{hint}</Description>}
			{hintChildren && hintChildren}
			{errorMessage && <FieldError>{errorMessage}</FieldError>}
			{isValid && successMessage && <FieldSuccess>{successMessage}</FieldSuccess>}
			<TextAreaPrimitive
				onFocus={() => setIsFocusWithin(true)}
				onBlur={() => setIsFocusWithin(false)}
				placeholder={placeholder}
				className={composeRenderProps(className, (className, renderProps) =>
					cn(
						fieldStyle({
							...renderProps,
							theme,
							width,
							variant,
							isValid,
							isFocusWithin,
						}),
						'min-h-24',
						className,
					),
				)}
			/>
		</TextFieldPrimitive>
	);
};

export type { TextAreaProps };
export { TextArea };
